import { ProjectLeadStatus } from "../types/projectLeadTypes";
import { ProjectBillingDetailsDto, ProjectStatus } from "../types/projectTypes";

export const PROJECT_COMPLETION_NOTE_MIN_LENGTH = 30;
export const PROJECT_CLOSURE_WAIVER_REASON_MIN_LENGTH = 15;

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  Lead: "Lead",
  Bewerbung: "Bewerbung",
  Angebot: "Angebot",
  Durchführung: "Durchführung",
  Abrechnung: "Abrechnung",
  Abgeschlossen: "Abgeschlossen",
  NichtBesetzt: "Nicht besetzt",
  PitchVerloren: "Pitch verloren",
  AngebotAbgelehnt: "Abgelehnt",
  Abgebrochen: "Abgebrochen",
};

export type ProjectKanbanStatus = Exclude<ProjectStatus, "NichtBesetzt"> | ProjectLeadStatus.Discarded;

export const PROJECT_KANBAN_STATUS_LABELS: Record<ProjectKanbanStatus, string> = {
  ...PROJECT_STATUS_LABELS,
  [ProjectLeadStatus.Discarded]: "Lead verworfen",
};

export const PROJECT_KANBAN_COLUMNS: {
  status: ProjectKanbanStatus;
  label: string;
  color: string;
}[] = [
  { status: "Lead", label: PROJECT_KANBAN_STATUS_LABELS.Lead, color: "#ed6c02" },
  { status: "Bewerbung", label: PROJECT_KANBAN_STATUS_LABELS.Bewerbung, color: "#2e7d32" },
  { status: "Angebot", label: PROJECT_KANBAN_STATUS_LABELS.Angebot, color: "#0288d1" },
  { status: "Durchführung", label: PROJECT_KANBAN_STATUS_LABELS.Durchführung, color: "#1976d2" },
  { status: "Abrechnung", label: PROJECT_KANBAN_STATUS_LABELS.Abrechnung, color: "#ad3d9b" },
  { status: "Abgeschlossen", label: PROJECT_KANBAN_STATUS_LABELS.Abgeschlossen, color: "#9c27b0" },
  // Keep rejected leads next to the other failed-outcome columns instead of up with active leads.
  {
    status: ProjectLeadStatus.Discarded,
    label: PROJECT_KANBAN_STATUS_LABELS[ProjectLeadStatus.Discarded],
    color: "#d32f2f",
  },
  { status: "PitchVerloren", label: PROJECT_KANBAN_STATUS_LABELS.PitchVerloren, color: "#ef6c00" },
  { status: "AngebotAbgelehnt", label: PROJECT_KANBAN_STATUS_LABELS.AngebotAbgelehnt, color: "#e53935" },
  { status: "Abgebrochen", label: PROJECT_KANBAN_STATUS_LABELS.Abgebrochen, color: "#d32f2f" },
];

export const getProjectStatusLabel = (status: string): string =>
  PROJECT_KANBAN_STATUS_LABELS[status as ProjectKanbanStatus] ?? status;

export const isApplicationPhaseStatus = (status: string): boolean => status === "Bewerbung";

export const canEditTenderingDates = (status: string): boolean => status === "Lead" || status === "Bewerbung";

export const isBillingStatus = (status: string): boolean =>
  status === "Durchführung" || status === "Abrechnung" || status === "Abgeschlossen";

/**
 * Returns whether the project can be transitioned from "Abrechnung" to "Abgeschlossen".
 * Mirrors the server-side checks in ProjectsService.completeProject.
 */
export const hasProjectCompletionNoteMinimumLength = (note: string | null | undefined): boolean =>
  (note?.trim().length ?? 0) >= PROJECT_COMPLETION_NOTE_MIN_LENGTH;

/**
 * Returns whether the project can be transitioned from "Abrechnung" to "Abgeschlossen".
 * Mirrors the server-side checks in ProjectsService.completeProject.
 */
export const canCompleteProject = (
  billingDetails: ProjectBillingDetailsDto | undefined | null,
  note?: string | null,
  projectClosureWaiverReason?: string | null,
  hasFollowUpActivity?: boolean
): boolean => {
  if (!billingDetails || billingDetails.status !== "Abrechnung") {
    return false;
  }
  if (!hasProjectCompletionNoteMinimumLength(note ?? billingDetails.projectCompletionNote)) {
    return false;
  }
  const checklistComplete =
    billingDetails.APatEV &&
    billingDetails.APHold &&
    billingDetails.evaluationAtEV &&
    billingDetails.DLatEV &&
    billingDetails.offerInAlfresco &&
    billingDetails.consultingContractProvided &&
    billingDetails.teamContractProvided &&
    billingDetails.qmApproval &&
    billingDetails.moneyReceived;
  if (!checklistComplete) {
    return false;
  }
  const membersReady = billingDetails.membersBilling.every(
    (member) => member.freelancerContract != null && member.moneyTransferred != null
  );
  if (!membersReady) {
    return false;
  }

  const waiverReasonLength =
    (projectClosureWaiverReason ?? billingDetails.projectClosureWaiverReason)?.trim().length ?? 0;
  return Boolean(hasFollowUpActivity || waiverReasonLength > 0);
};
